import { type FC, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { UrlObject } from 'url';
import type { Analytics, SportSB } from '@mwl/core-lib';
import { selectSportsWithLines, selectUserIsAuthenticated, useAccessCookie, useItemsFilteredByStage, useSportUrlParams } from '@mwl/core-lib';
import { NoSSR } from '@mwl/ui';
import FavoritesIcon from '@public/assets/sport/icons/favorites.svg';
import TopIcon from '@public/assets/sport/icons/top.svg';
import type { ButtonProps } from '@/components/Button/Button';
import { Button } from '@/components/Button/Button';
import { PerfectScrollbarWithShadow } from '@/components/PerfectScrollbarWithShadow/PerfectScrollbarWithShadow';
import { SportIcon } from '@/components/SportIcon/SportIcon';
import { routes } from '@/constants';
import { useIsCyberSportPage, useSportSidebarBehavior, useTypedDispatch, useTypedSelector } from '@/hooks';
import { setShowTabletSportSidebar } from '@/store';
import { additionalSportListAnalytics, getBaseSportListAnalytics } from './SportList.analytics';
import styles from './SportList.module.scss';
export const SportList: FC = () => {
  const {
    t
  } = useTranslation('sport');
  const {
    pathname
  } = useRouter();
  const dispatch = useTypedDispatch();
  const hasCyberCookie = useAccessCookie('cyber');
  const isCyberSportPage = useIsCyberSportPage();
  const {
    sidebarIsOpened,
    isTabletScreen
  } = useSportSidebarBehavior();
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const sports = useTypedSelector(state => selectSportsWithLines(state, isCyberSportPage ? 'CYBER' : undefined));
  const {
    sportId,
    sportIds,
    createSportItemLink,
    stage
  } = useSportUrlParams({
    isCyberSportPage,
    routes
  });
  const filteredSports = useItemsFilteredByStage<SportSB>(sports, stage);
  const isActive = useCallback((sportItem: SportSB) => {
    return sportItem.id === sportId;
  }, [sportId]);
  const handleToggleSidebar = useCallback(() => {
    dispatch(setShowTabletSportSidebar(!sidebarIsOpened));
  }, [dispatch, sidebarIsOpened]);
  const {
    topsAnalytics,
    favoritesAnalytics
  } = additionalSportListAnalytics;
  const getAnalyticsData = useCallback((sportItem: SportSB, href: UrlObject): Analytics<'click'> => {
    const {
      baseSportsAnalytics
    } = getBaseSportListAnalytics(sportItem, href, filteredSports);
    if (isActive(sportItem)) {
      return {
        click: {
          eventName: baseSportsAnalytics.unselect?.eventName,
          data: baseSportsAnalytics.unselect?.data
        }
      };
    }
    return {
      click: {
        eventName: baseSportsAnalytics.select?.eventName,
        data: baseSportsAnalytics.select?.data
      }
    };
  }, [filteredSports, isActive]);
  const getButtonProps = useCallback((item: SportSB): ButtonProps => {
    if (isTabletScreen && sportId === item.id) {
      return {
        as: 'button',
        onClick: handleToggleSidebar
      };
    }
    return {
      as: 'link',
      href: createSportItemLink(item.id)
    };
  }, [createSportItemLink, handleToggleSidebar, isTabletScreen, sportId]);
  return <div className={styles.root} data-sentry-component="SportList" data-sentry-source-file="SportList.tsx">
      <PerfectScrollbarWithShadow className={styles.scrollbar} options={{
      wheelPropagation: false
    }} isShowShadow isSwitchOffOnMobile={false} shadowVariant="dark80Shadow" data-sentry-element="PerfectScrollbarWithShadow" data-sentry-source-file="SportList.tsx">
        {!isCyberSportPage && <Button as="link" href={routes.sport.home} className={cn(styles.item, {
        [styles.active]: pathname === routes.sport.home && !stage && !sportId && !sportIds
      })} align="center" fullWidth color="white" variant="text" shallow={routes.sport.home === pathname} analytics={topsAnalytics}>
            <span className={styles.iconWrapper}>
              <TopIcon className={styles.icon} />
            </span>
            <span className={styles.label}>{t('sport_tops')}</span>
          </Button>}
        {isAuth && (!isCyberSportPage || hasCyberCookie && isCyberSportPage) && <NoSSR>
            <Button className={cn(styles.item, {
          [styles.active]: [routes.sport.favorites, routes.esports.favorites].includes(pathname)
        })} as="link" href={isCyberSportPage ? routes.esports.favorites : routes.sport.favorites} align="center" fullWidth color="white" variant="text" analytics={favoritesAnalytics}>
              <span className={styles.iconWrapper}>
                <FavoritesIcon className={styles.icon} />
              </span>
              <span className={styles.label}>{t('sport_favorites', 'Favorites')}</span>
            </Button>
          </NoSSR>}
        {filteredSports.map(sportItem => {
        const {
          id,
          title
        } = sportItem;
        const href = createSportItemLink(sportItem.id);
        return <Button key={id} className={cn(styles.item, {
          [styles.active]: isActive(sportItem)
        })} {...getButtonProps(sportItem)} align="center" fullWidth color="white" variant="text" fontWeight="medium" analytics={getAnalyticsData(sportItem, href)}>
              <SportIcon className={styles.icon} url={sportItem.icon} size={24} isActive={isActive(sportItem)} />
              <span className={styles.label}>{title}</span>
            </Button>;
      })}
      </PerfectScrollbarWithShadow>
    </div>;
};